// B01: a legacy submit (video or avatar) is one *intent*. It must not be submitted twice by a
// double click, a lost response, a reload, or a retry. Pure helpers, no imports, so they are
// testable without a browser.

// Synchronous claim: a React state check alone is stale until the next render, so two
// invocations in the same tick would both pass it.
export const createSubmitGuard = () => {
  let busy = false;
  return {
    tryClaim() {
      if (busy) return false;
      busy = true;
      return true;
    },
    release() { busy = false; },
    get busy() { return busy; },
  };
};

const sortKeys = (value) => {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, sortKeys(value[key])]));
  }
  return value;
};

// Small non-cryptographic fingerprint (cyrb53): identifies "the same inputs" without keeping the
// script text in storage. It is not a security boundary; the backend scopes everything by user.
export const intentSignature = (intent) => {
  const text = JSON.stringify(sortKeys(intent));
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
};

// One receipt per user and kind. Without a signed-in user there is no safe scope.
export const legacyReceiptKey = (kind, userId) =>
  (userId && (kind === "video" || kind === "avatar")) ? `legacy-submit:${kind}:${userId}` : null;

export const newLegacyRequestId = (cryptoApi = globalThis.crypto) =>
  (cryptoApi?.randomUUID ? cryptoApi.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const readLegacyReceipt = (storage, key) => {
  if (!key) return null;
  try {
    const value = JSON.parse(storage.getItem(key) || "null");
    if (value && value.version === 1 && typeof value.signature === "string"
        && typeof value.request_id === "string" && /^[A-Za-z0-9_-]{8,100}$/.test(value.request_id)) return value;
  } catch { /* unreadable storage or malformed JSON: treat as no receipt */ }
  return null;
};

// The request id is persisted BEFORE the request is sent, so a reload or a lost response can
// retry with the same id and the backend replays instead of creating a second job.
export const prepareLegacyReceipt = (storage, key, signature, makeId = newLegacyRequestId) => {
  if (!key) throw new Error("Sesi pengguna belum tersedia. Muat ulang sebelum mengirim.");
  const existing = readLegacyReceipt(storage, key);
  if (existing && existing.signature === signature) return { ...existing, reused: true };
  const receipt = { version: 1, request_id: makeId(), signature, created_at: Date.now() };
  try { storage.setItem(key, JSON.stringify(receipt)); }
  catch { throw new Error("Permintaan belum dapat disimpan. Izinkan penyimpanan sesi lalu coba lagi."); }
  return { ...receipt, reused: false };
};

export const clearLegacyReceipt = (storage, key) => {
  if (!key) return;
  try { storage.removeItem(key); } catch { /* a retained receipt only makes a retry idempotent */ }
};

// "definitive": the server rejected the request before creating anything, so the receipt can be
// dropped and the next attempt is a new intent. "ambiguous": the job may exist (no response, a
// gateway error, or a 5xx we do not recognise), so the receipt is KEPT and a retry replays it.
// `definitiveDetails` maps an HTTP status to the exact server messages that prove a rejection.
export const classifySubmitError = (error, definitiveDetails = {}) => {
  const response = error?.response;
  if (!response) return "ambiguous";
  const status = Number(response.status);
  const detail = typeof response.data?.detail === "string" ? response.data.detail : "";
  if (status >= 400 && status < 500 && ![408, 425, 429].includes(status)) return "definitive";
  if ((definitiveDetails[status] || []).includes(detail)) return "definitive";
  return "ambiguous";
};

export const VIDEO_DEFINITIVE_DETAILS = {
  502: ["Video gagal dibuat. Kredit yang dicadangkan telah dikembalikan."],
  503: ["Fitur video avatar HeyGen sedang dinonaktifkan sementara."],
};

export const AVATAR_DEFINITIVE_DETAILS = {
  502: ["Gagal mengunggah foto ke HeyGen. Silakan coba lagi.", "Gagal membuat avatar. Silakan coba lagi."],
  503: ["Fitur video avatar HeyGen sedang dinonaktifkan sementara."],
};

export const LEGACY_RECONCILING_NOTICE =
  "Hasil pengiriman video belum dapat dipastikan. Kredit ditahan sementara dan tidak dikembalikan otomatis. Jangan kirim ulang; status akan diperbarui setelah diperiksa.";
